import styled from '@emotion/styled';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import FormError from './oc-form-error';
import withDefaultPrevented from '../../../withDefaultPrevented';
import { H1, H4 } from '../../OC/oc-h';
import FormButton from '../input-elements/oc-form-button';

// Styled Components
const Page = styled.div`
  display: ${props => (props.active ? 'inline-block' : 'none')};
  position: relative;
  width: 100%;
`;

const PageHeader = styled.div`
  border-bottom: ${props => (props.hideBorder ? '0px' : '1px solid #000000')};
  display: inline-block;
  margin-bottom: 40px;
  position: relative;
  width: 100%;
`;

const HeadingContainer = styled.div`
  display: flex;
  position: relative;

  @media screen and (max-width: 599px) {
    flex-wrap: wrap;
  }
`;

const TextContainer = styled.div`
  display: inline-block;
  flex-grow: 1;
  position: relative;

  @media screen and (max-width: 599px) {
    width: 100%;
  }
`;

const SkipContainer = styled.div`
  display: inline-block;
  flex-shrink: 0;
  position: relative;

  @media screen and (max-width: 599px) {
    margin-bottom: 16px;
  }

  @media screen and (min-width: 600px) {
    padding-left: 16px;
  }
`;

const PreHead = styled.span`
  color: #333333;
  display: inline-block;
  line-height: 18px;
  margin-bottom: 5px;
  width: 100%;
`;

const StyledH1 = styled(H1)`
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;

  .pink {
    color: #d40f7d;
  }
`;

const StyledH4 = styled(H4)`
  html body & {
    color: ${props => props.themeColor};
    margin-bottom: 16px;
    text-transform: uppercase;
  }
`;

const Description = styled.p`
  margin-top: 0;
`;

const Cta = styled.a`
  align-items: center;
  cursor: pointer;
  display: inline-flex;
  font-size: 18px;
  justify-content: space-between;
  margin-bottom: 1em;
  position: relative;
  text-decoration: none;
  transition: all 0.35s ease-in-out;

  &:after {
    content: '';
    font-family: icomoon;
    margin-left: 12px;
    position: relative;
  }
`;

const FormPage = ({
  active,
  children,
  cta,
  data,
  error,
  errorLabel = null,
  errorChildren,
  description,
  openFormModal,
  prehead,
  title,
  titlePinkText,
  optionalStep,
  skipFunction,
  themeColor,
  hideBorder = false,
}) => {
  const skipLinkTrack = data?.skipLink || 'skip-step';
  return (
    <Page active={active}>
      <PageHeader hideBorder={hideBorder}>
        <HeadingContainer>
          <TextContainer>
            {prehead && <PreHead>{prehead}</PreHead>}
            <StyledH1 className="tier3">
              <span>{title}</span>
              {titlePinkText && <span className="pink">{titlePinkText}</span>}
            </StyledH1>
            {optionalStep && <StyledH4 themeColor={themeColor}>Please note: this step is optional</StyledH4>}
          </TextContainer>

          {optionalStep && (
            <SkipContainer>
              <FormButton
                color="#FFFFFF"
                data-track={skipLinkTrack}
                onClick={event => skipFunction(event, { trigger: 'skipLink' })}
              >
                Skip this Step
              </FormButton>
            </SkipContainer>
          )}
        </HeadingContainer>

        {description && <Description dangerouslySetInnerHTML={{ __html: description }} />}

        {cta && (
          <Cta
            data-track={cta.data && cta.data.track}
            data-track-destination={cta.data && cta.data.trackDestination}
            href={cta.href || '#'}
            onClick={cta.infoModalContent ? withDefaultPrevented(() => openFormModal(cta.infoModalContent)) : null}
            target={cta.href && '_blank'}
            title={cta.text}
          >
            {cta.text}
          </Cta>
        )}
      </PageHeader>

      {error && (
        <FormError message={error} errorLabel={errorLabel}>
          {errorChildren && errorChildren}
        </FormError>
      )}

      {children}
    </Page>
  );
};

export default FormPage;

FormPage.propTypes = {
  /* CSS: Determines active state & display properties */
  active: PropTypes.bool,
  /* JSX: Form content, inputs, etc */
  children: PropTypes.any,
  /* HTML: The optional paragraph content to display under the page title */
  description: PropTypes.string,
  /* HTML: The optional prehead displayed above the page title */
  prehead: PropTypes.string,
  /* HTML: The main heading that displays as the page title */
  title: PropTypes.string,
  /* HTML: Form error text that appears at the top of the page */
  error: PropTypes.node,
  /* HTML: Optional extra markup for the form error, ex: including a clickable CTA */
  errorChildren: PropTypes.node,
  /* HTML: Optional array to display a custom heading needed by quoting widget */
  customHead: PropTypes.array,
  /* CSS: Hides the bottom border on the page header element */
  hideBorder: PropTypes.bool,
};
